import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Avatar,
  Paper
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { eventsService } from '../services/api';

const ResultsListing = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const hasLoadedRef = useRef(false);

  const loadPublishedResults = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use lightweight results summary API for better performance
      const response = await eventsService.getResultsSummary({ limit: 50 });
      
      // Handle the double-nested data structure from makeApiCall
      const apiData = response.data?.data || response.data;
      const eventsWithResults = apiData?.events || [];
      
      setEvents(eventsWithResults);
      
    } catch (err) {
      console.error('Error loading published results:', err);
      setError('Failed to load contest results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array to make function stable

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadPublishedResults();
    }
  }, [loadPublishedResults]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4} textAlign="center">
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          🏆 Contest Results
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Discover the winners and finalists from our writing contests
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Grid */}
      {events.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TrophyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Contest Results Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Contest results will appear here once they are published by our administrators.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/events')}
            sx={{ mr: 2 }}
          >
            Browse Active Contests
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/stories')}
          >
            Read Stories
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {events.map((event) => (
            <Grid item xs={12} key={event.event_id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  },
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/events/${event.event_id}/results`)}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Contest Header */}
                  <Box display="flex" alignItems="flex-start" mb={2}>
                    {/* Status Dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'success.main',
                        mr: 1.5,
                        mt: 0.75,
                        flexShrink: 0
                      }}
                    />
                    
                    {/* Title and Status */}
                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {event.title}
                      </Typography>
                    </Box>
                    
                    <Chip 
                      label="Results Published" 
                      color="success" 
                      size="small"
                      icon={<TrophyIcon />}
                    />
                  </Box>
                  
                  {/* Contest Description */}
                  {event.description && (
                    <Box sx={{ ml: 2.5, mb: 2 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {event.description}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Contest Details Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(event.start_date)} - {formatDate(event.end_date)}
                      </Typography>
                    </Box>
                    
                    {event.theme && (
                      <Chip 
                        label={`Theme: ${event.theme}`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  {/* Contest Stats Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {event.participant_count || 0} participants
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArticleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {event.submission_count || 0} submissions
                      </Typography>
                    </Box>
                    
                    {/* View Results Button */}
                    <Box sx={{ ml: 'auto' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/events/${event.event_id}/results`);
                        }}
                        sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          }
                        }}
                      >
                        View Results
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Call to Action */}
      {events.length > 0 && (
        <Box mt={6} textAlign="center">
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Want to Participate?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Join our active writing contests and showcase your creativity!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/events')}
              sx={{ mr: 2 }}
            >
              Browse Active Contests
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/stories')}
            >
              Read Stories
            </Button>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default ResultsListing;
